import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MessageCircle, Heart, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function Matches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [likers, setLikers] = useState([]);
  const [tab, setTab] = useState('matches'); // matches | likes
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [mData, lData] = await Promise.all([
          api('/matches'),
          api('/likes/received'),
        ]);
        setMatches(mData.matches || []);
        setLikers(lData.likers || []);
      } catch (err) {
        toast.error(err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAct = async (liker, action) => {
    if (actingId) return;
    setActingId(liker.id);
    try {
      const res = await api('/likes', {
        method: 'POST',
        body: JSON.stringify({ to_user_id: liker.id, action }),
      });
      setLikers((prev) => prev.filter((l) => l.id !== liker.id));
      if (action === 'like' && res?.match_created) {
        toast.success(`It's a match with ${liker.name}! 🎉`);
        // Refresh matches list so the new match appears
        try {
          const mData = await api('/matches');
          setMatches(mData.matches || []);
        } catch { /* non-fatal */ }
        setTab('matches');
      } else if (action === 'like') {
        toast.success(`You liked ${liker.name}`);
      } else {
        toast(`Passed on ${liker.name}`);
      }
    } catch (err) {
      toast.error(err.message || `Could not ${action}`);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="matches-page">
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
        Matches
      </h1>
      <p className="text-sm text-[#6D6875] mb-5">Your connections and people who liked you</p>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white border border-[#F4F1DE] rounded-full p-1">
        <button
          onClick={() => setTab('matches')}
          data-testid="tab-matches"
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition-colors ${
            tab === 'matches' ? 'bg-[#C8553D] text-white' : 'text-[#6D6875]'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          Matches ({matches.length})
        </button>
        <button
          onClick={() => setTab('likes')}
          data-testid="tab-likes"
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition-colors ${
            tab === 'likes' ? 'bg-[#C8553D] text-white' : 'text-[#6D6875]'
          }`}
        >
          <Heart className="w-4 h-4" />
          Likes ({likers.length})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="matches-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" />
        </div>
      ) : tab === 'matches' ? (
        matches.length === 0 ? (
          <EmptyState text="No matches yet" sub="Keep swiping to find your people!" testid="matches-empty" />
        ) : (
          <ul className="space-y-3" data-testid="matches-list">
            {matches.map(({ match, user: other, last_message }) => (
              <li key={match.id} data-testid={`match-row-${match.id}`}>
                <Link
                  to={`/app/chat/${match.id}`}
                  state={{ otherUser: other, match }}
                  className="flex items-center gap-4 p-3 bg-white border border-[#F4F1DE] rounded-2xl hover:bg-[#FFE5DD]/30 transition-colors"
                >
                  <Avatar user={other} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-[#2B2D42] truncate">{other.name}</h3>
                      {last_message && (
                        <span className="text-[11px] text-[#B5B5B5] flex-shrink-0">
                          {timeAgo(last_message.created_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#6D6875] truncate">
                      {last_message
                        ? (last_message.sender_id === user?.id ? 'You: ' : '') + last_message.text
                        : `Say hi to ${other.name.split(' ')[0]}!`}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : likers.length === 0 ? (
        <EmptyState text="No likes yet" sub="They'll show up here when someone likes you" testid="likes-empty" />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="likes-list">
          {likers.map((liker) => {
            const isActing = actingId === liker.id;
            return (
              <li key={liker.id} className="bg-white border border-[#F4F1DE] rounded-2xl overflow-hidden flex flex-col" data-testid={`liker-card-${liker.id}`}>
                <div className="aspect-[4/5] bg-[#FFE5DD] relative">
                  {liker.photos?.[0] ? (
                    <img src={liker.photos[0]} alt={liker.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-5xl font-serif font-bold text-[#C8553D]/40" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {liker.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
                    <Sparkles className="w-3 h-3 text-[#E76F51]" />
                    <span className="text-[10px] font-bold text-[#2B2D42] uppercase tracking-wide">Liked you</span>
                  </div>
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <p className="font-bold text-sm text-[#2B2D42] truncate">{liker.name}{liker.age ? `, ${liker.age}` : ''}</p>
                  <p className="text-xs text-[#6D6875] truncate mb-3">{liker.city || liker.country_of_origin || ''}</p>
                  <div className="mt-auto grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid={`liker-pass-${liker.id}`}
                      onClick={() => handleAct(liker, 'pass')}
                      disabled={isActing}
                      className="flex items-center justify-center gap-1 py-2 rounded-full border-2 border-[#E0DDD7] text-[#6D6875] hover:bg-[#F4F1DE] disabled:opacity-50 transition-colors text-xs font-bold"
                    >
                      <X className="w-3.5 h-3.5" />
                      Pass
                    </button>
                    <button
                      type="button"
                      data-testid={`liker-like-back-${liker.id}`}
                      onClick={() => handleAct(liker, 'like')}
                      disabled={isActing}
                      className="flex items-center justify-center gap-1 py-2 rounded-full bg-[#C8553D] hover:bg-[#9E3A26] text-white disabled:opacity-50 transition-colors text-xs font-bold shadow shadow-[#C8553D]/30"
                    >
                      {isActing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5 fill-white" />}
                      Like Back
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Avatar({ user }) {
  const photo = user?.photos?.[0];
  return (
    <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-[#FFE5DD]">
      {photo ? (
        <img src={photo} alt={user.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xl font-serif font-bold text-[#C8553D]/40" style={{ fontFamily: 'Playfair Display, serif' }}>
          {user?.name?.charAt(0) || '?'}
        </div>
      )}
    </div>
  );
}

function EmptyState({ text, sub, testid }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-2xl border border-[#F4F1DE]" data-testid={testid}>
      <Heart className="w-12 h-12 text-[#FFE5DD] mb-3" />
      <h3 className="text-lg font-bold text-[#2B2D42]">{text}</h3>
      <p className="text-sm text-[#6D6875] mt-1">{sub}</p>
    </div>
  );
}
